import { useDropzone } from "@msuite/ui/file-upload";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { ProgressMeter } from "@msuite/ui/progress-meter";
import { toast } from "@msuite/ui/toast";
import { cn } from "@msuite/ui/utils";
import { useState } from "react";

/** The reasons the upload can give for failing, each with its own sentence. */
const FAILURE_KEYS = [
	"videos.insert_image_failed",
	"videos.insert_video_failed",
	"videos.upload_failed",
] as const;
type FailureKey = (typeof FAILURE_KEYS)[number];

/** Props Interface */
interface UploadMediaModalProps extends ModalProps {
	/**
	 * Puts one chosen file into the page. It resolves once the file is where it belongs — an
	 * image linked and its block saved, a video handed to its host — and rejects with a message
	 * key when it is not. Progress is a fraction of one.
	 */
	onUpload: (file: File, onProgress: (fraction: number) => void) => Promise<void>;
}

/**
 * The dialog behind "Bild/Video hochladen": one dropzone, one file, and the upload's progress
 * while it runs. It sits beside the recorder's dialog rather than inside it, because the two
 * answer different questions — what to capture, versus which file — and a recording's options
 * have nothing to say about a file that already exists.
 *
 * The dialog cannot be closed while a file is on its way: closing would abandon an upload that
 * already has a block in the page waiting for it.
 */
export function UploadMediaModal({ isOpen, onClose, onUpload }: UploadMediaModalProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** State */
	const [fileName, setFileName] = useState<string | null>(null);
	const [progress, setProgress] = useState(0);

	/** Functions */
	async function handleOnDrop(accepted: File[], rejected: unknown[]) {
		if (rejected.length > 0) toast.error(t("videos.only_images_and_videos"));
		const file = accepted[0];
		if (!file || fileName) return;
		setFileName(file.name);
		setProgress(0);
		try {
			await onUpload(file, setProgress);
			onClose();
		} catch (error) {
			console.error("Uploading the file failed:", error);
			const message = error instanceof Error ? error.message : "";
			const key: FailureKey = (FAILURE_KEYS as readonly string[]).includes(message)
				? (message as FailureKey)
				: "videos.upload_failed";
			toast.error(t(key));
		} finally {
			setFileName(null);
			setProgress(0);
		}
	}

	const dropzone = useDropzone({
		onDrop: handleOnDrop,
		accept: { "image/*": [], "video/*": [] },
		multiple: false,
		disabled: !!fileName,
	});

	/** Computed */
	const isUploading = !!fileName;

	/** Render */
	return (
		<Modal
			isOpen={isOpen}
			onClose={isUploading ? () => {} : onClose}
			title={t("videos.upload_media")}
			width="40rem"
			closeOnEsc={!isUploading}
		>
			<Modal.Body>
				<input
					{...dropzone.getInputProps()}
					className="hidden"
					data-cy="upload-media"
				/>
				<div
					{...(dropzone.getRootProps() as Omit<ReturnType<typeof dropzone.getRootProps>, "role">)}
					role="none"
					className={cn(
						"card flex flex-col items-center gap-3 py-10 text-center",
						isUploading ? "cursor-default" : "cursor-pointer",
						dropzone.isDragActive && "border-dashed!",
						dropzone.isDragAccept && "border-primary-500!",
						dropzone.isDragReject && "border-red-500!",
					)}
				>
					{isUploading ? (
						<div className="v-stack w-full max-w-sm items-center gap-3">
							<p className="truncate text-sm">{fileName}</p>
							<div className="w-full">
								<ProgressMeter value={Math.round(progress * 100)} />
							</div>
							<p className="text-xs opacity-60">{t("videos.uploading")}</p>
						</div>
					) : (
						<>
							<Icon
								name="Upload"
								className="opacity-60"
							/>
							<p className="text-sm">{t("videos.upload_media_hint")}</p>
							<p className="text-xs opacity-60">{t("videos.only_images_and_videos")}</p>
						</>
					)}
				</div>
			</Modal.Body>
		</Modal>
	);
}
